import React, { ReactNode, useState } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { noop } from 'lodash';

import Cancel from '@mui/icons-material/Cancel';
import DragHandle from '@mui/icons-material/DragHandle';
import Edit from '@mui/icons-material/Edit';
import { IconButton, TableCell, TableRow } from '@mui/material';

import { ChildrenLock } from './helpers';

type DraggableRowProps = {
  children: ReactNode;
  id: string;
  index: number;
  cellClass?: string;
  dragDisabled?: boolean;
  singleValue?: boolean;
  canEdit?: boolean;
  onEdit?: (index: number) => void;
  canDelete?: boolean;
  onRemove?: (index: number) => void;
};

const DraggableRow = ({
  id,
  index,
  children,
  canDelete = true,
  dragDisabled = false,
  onRemove = noop,
  singleValue = false,
  canEdit = false,
  onEdit = noop,
}: DraggableRowProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const visibility = React.useMemo(() => (isHovered ? 'visible' : 'hidden'), [isHovered]);

  const handleRemove = React.useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);

  const handleEdit = React.useCallback(() => {
    onEdit(index);
  }, [index, onEdit]);

  return (
    <Draggable draggableId={id} index={index} isDragDisabled={dragDisabled}>
      {({ innerRef, draggableProps, dragHandleProps }, { isDragging }) => (
        <TableRow
          data-qa={`row-${index}`}
          ref={innerRef}
          sx={{ bgcolor: 'white', boxShadow: isDragging ? 5 : 0 }}
          onMouseOver={(event) => {
            // @ts-ignore
            !isHovered && event?.relatedTarget?.tagName !== 'TABLE' && setIsHovered(true);
          }}
          onPointerLeave={() => setIsHovered(false)}
          {...draggableProps}
        >
          <ChildrenLock>
            {!dragDisabled && (
              <TableCell align="center" data-qa="drag-cell" key="dragCell" sx={{ width: 1 / 100 }}>
                <IconButton {...dragHandleProps}>
                  <DragHandle />
                </IconButton>
              </TableCell>
            )}
            {dragDisabled && <TableCell data-qa="drag-cell" />}
            {children}
            {canEdit && (
              <TableCell align="center" data-qa="edit-cell" key="editCell" sx={{ width: 1 / 100 }}>
                <IconButton disabled={singleValue} onClick={handleEdit} sx={{ visibility }}>
                  <Edit />
                </IconButton>
              </TableCell>
            )}
            {canDelete && (
              <TableCell align="center" data-qa="remove-cell" key="removeCell" sx={{ width: 1 / 100 }}>
                <IconButton disabled={singleValue} onClick={handleRemove} sx={{ visibility }}>
                  <Cancel color="error" />
                </IconButton>
              </TableCell>
            )}
          </ChildrenLock>
        </TableRow>
      )}
    </Draggable>
  );
};

export { DraggableRow };
